import React, { useEffect, useState, useContext } from "react";
import { AdminAuthContext } from '../../AdminAuthContext';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiAlertTriangle, FiDollarSign, FiEdit3, FiGlobe, FiPercent, FiPlus, FiSave, FiTrash2 } from 'react-icons/fi';
import './AdminUI.css';

const AdminCountries = () => {
  const { admin, loading } = useContext(AdminAuthContext);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && admin && admin.role === 'admin') {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        localStorage.removeItem('admin_token');
        navigate('/admin-login');
        return;
      }
      axios.get("https://valleyapi-dr9y.onrender.com/api/countries", { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => {
          setCountries(response.data || []);
          setError(response.data?.length ? null : 'No country data received');
        })
        .catch((fetchError) => {
          console.error("Error loading countries:", fetchError.message);
          if (fetchError.response?.status === 401) {
            localStorage.removeItem('admin_token');
            navigate('/admin-login');
          } else {
            setError(`Countries could not be loaded: ${fetchError.message}`);
          }
        });
    }
  }, [loading, admin, navigate]);

  const handleInputChange = (event, index, field) => {
    const updated = [...countries];
    updated[index] = { ...updated[index], [field]: event.target.value ? parseFloat(event.target.value) : '' };
    setCountries(updated);
    setHasChanges(true);
  };

  const handleUpdate = async () => {
    if (!window.confirm('Save the updated shipping and tax values?')) return;
    const token = localStorage.getItem('admin_token');
    if (!token) return navigate('/admin-login');
    setSaving(true);
    try {
      await axios.put("https://valleyapi-dr9y.onrender.com/api/countries/update", countries, { headers: { Authorization: `Bearer ${token}` } });
      setHasChanges(false);
      alert("Country settings updated successfully.");
    } catch (updateError) {
      console.error("Error updating countries:", updateError.message);
      if (updateError.response?.status === 401) navigate('/admin-login');
      else alert(`The changes could not be saved: ${updateError.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, countryName) => {
    if (!window.confirm(`Delete ${countryName}? This action cannot be undone.`)) return;
    const token = localStorage.getItem('admin_token');
    if (!token) return navigate('/admin-login');
    try {
      await axios.delete(`https://valleyapi-dr9y.onrender.com/api/countries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCountries((current) => current.filter((country) => country.idPais !== id));
      setError(null);
    } catch (deleteError) {
      console.error("Error deleting country:", deleteError.message);
      setError(`Country could not be deleted: ${deleteError.response?.data?.error || deleteError.message}`);
      if (deleteError.response?.status === 401) navigate('/admin-login');
    }
  };

  if (loading) return <div className="va-page va-card va-loading"><div className="va-spinner" aria-label="Loading countries" /></div>;
  if (!admin || admin.role !== 'admin') return <div className="va-page va-card va-state"><div><FiAlertTriangle /><strong>Access denied</strong><p>Administrator privileges are required.</p></div></div>;

  const averageShipping = countries.length ? countries.reduce((sum, country) => sum + Number(country.Shipping_Cost || 0), 0) / countries.length : 0;
  const averageTax = countries.length ? countries.reduce((sum, country) => sum + Number(country.Taxes || 0), 0) / countries.length : 0;

  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Regional configuration</span>
          <h1 className="va-title">Countries</h1>
          <p className="va-subtitle">Configure shipping costs and tax rates for every destination served by Valley.</p>
        </div>
        <div className="va-header-actions">
          <button className="va-button va-button--secondary" onClick={() => navigate('/admin/countries/add')}><FiPlus /> Add country</button>
          <button className="va-button" onClick={handleUpdate} disabled={!hasChanges || saving}><FiSave /> {saving ? 'Saving...' : 'Save changes'}</button>
        </div>
      </header>

      <div className="va-stats">
        <div className="va-stat"><span className="va-stat__icon"><FiGlobe /></span><div><strong>{countries.length}</strong><span>Active destinations</span></div></div>
        <div className="va-stat"><span className="va-stat__icon"><FiDollarSign /></span><div><strong>${averageShipping.toFixed(2)}</strong><span>Average shipping cost</span></div></div>
        <div className="va-stat"><span className="va-stat__icon"><FiPercent /></span><div><strong>{averageTax.toFixed(2)}%</strong><span>Average tax rate</span></div></div>
        <div className="va-stat"><span className="va-stat__icon"><FiEdit3 /></span><div><strong>{hasChanges ? 'Unsaved' : 'Current'}</strong><span>Configuration status</span></div></div>
      </div>

      {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}

      <div className="va-table-wrap">
        <table className="va-table">
          <thead><tr><th>Destination</th><th>Shipping cost</th><th>Tax rate</th><th></th></tr></thead>
          <tbody>
            {countries.length > 0 ? countries.map((country, index) => (
              <tr key={country.idPais}>
                <td><span className="va-table__primary">{country.Country}</span><span className="va-table__secondary">Country ID #{country.idPais}</span></td>
                <td><div className="va-field" style={{ maxWidth: 180 }}><input type="number" step="0.01" min="0" value={country.Shipping_Cost ?? ''} onChange={(event) => handleInputChange(event, index, 'Shipping_Cost')} className="va-input" aria-label={`Shipping cost for ${country.Country}`} /></div></td>
                <td><div className="va-field" style={{ maxWidth: 180 }}><input type="number" step="0.01" min="0" value={country.Taxes ?? ''} onChange={(event) => handleInputChange(event, index, 'Taxes')} className="va-input" aria-label={`Tax rate for ${country.Country}`} /></div></td>
                <td><div className="va-table__actions"><button className="va-button va-button--ghost va-button--icon" onClick={() => handleDelete(country.idPais, country.Country)} title="Delete country" aria-label={`Delete ${country.Country}`}><FiTrash2 /></button></div></td>
              </tr>
            )) : (
              <tr><td colSpan="4"><div className="va-empty"><div><FiGlobe /><strong>No countries configured</strong><p>Add a destination to define its shipping and taxes.</p></div></div></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCountries;
